import { useDisclosure } from '@chakra-ui/react';
import {
  AmountUnitTypeEnum,
  DiscountApplicationMethodEnum,
  DiscountLifeCyclePolicyEnum,
  IQuoteItemRespSchema,
  IQuoteOfferingReqSchema,
  IQuoteRespSchema,
  ProductTypeEnum,
  TQuotePrice,
} from '@monetize/types/app';
import { formatPercentNumerator, toDateShort } from '@monetize/utils/core';
import React, { useMemo } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { LuRefreshCwOff } from 'react-icons/lu';
import {
  MDivider,
  MFlex,
  MFormField,
  MIcon,
  MText,
  MTooltip,
} from '../../../../../components/Monetize';
import { DiscountCell } from '../../../../../components/Quotes';
import {
  getDiscountText,
  isAnyDiscountExcludedOnRenewal,
  multipleDiscountType,
  sortAlphabetically,
} from '../../../../../utils';
import { getDiscountPercentForQuoteItem } from '../../../../../utils/quotes';
import { useCustomDiscountExcludedOnRenewal } from '../../hooks/useDiscountExcludedOnRenewal';
import { QuoteItemDiscountEditModal } from './QuoteItemDiscountEditModal';

export interface QuoteItemDiscountInputAndValueProps {
  index: number;
  errors: FieldErrors<IQuoteOfferingReqSchema>;
  isDisabled?: boolean;
  /** Locked for good, as opposed to `isDisabled`'s mid-save states. */
  isReadOnly?: boolean;
  control: Control<IQuoteOfferingReqSchema>;
  setValue: UseFormSetValue<IQuoteOfferingReqSchema>;
  itemFromServer: IQuoteItemRespSchema;
  quote: IQuoteRespSchema;
  customDiscountType?: AmountUnitTypeEnum | null;
  discountAmountRef: React.MutableRefObject<HTMLInputElement | null>;
  onChangeDiscountType: (type: AmountUnitTypeEnum) => void;
  handleQuoteOffering: () => void;
  disableUnitTypeSelect?: boolean;
  disableUnitTypeMessage?: string;
  offeringId?: string;
  usagePrice: TQuotePrice | null;
}

export const QuoteItemDiscountInputAndValue = ({
  errors,
  isDisabled,
  isReadOnly,
  control,
  setValue,
  index,
  quote,
  customDiscountType,
  discountAmountRef,
  itemFromServer,
  onChangeDiscountType,
  handleQuoteOffering,
  disableUnitTypeSelect,
  disableUnitTypeMessage,
  offeringId,
  usagePrice,
}: QuoteItemDiscountInputAndValueProps) => {
  const {
    open: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
  } = useDisclosure();

  const hasCatalogDiscounts = itemFromServer.discounts?.length > 0;
  const targetAmount = useMemo(() => {
    if (itemFromServer.productType === ProductTypeEnum.USAGE) {
      return usagePrice?.amount ?? 0;
    }

    return itemFromServer.amountWithoutDiscount;
  }, [
    itemFromServer.amountWithoutDiscount,
    itemFromServer.productType,
    usagePrice?.amount,
  ]);

  const customDiscountAmountOrPercent = useWatch({
    control,
    name: `items.${index}.customDiscountAmountOrPercent`,
  });

  const totalCatalogDiscount = itemFromServer.discounts?.reduce(
    (acc, discount) => acc + (discount.discountAmountOrPercent ?? 0),
    0,
  );

  const { offeringPeriod, billingFrequencyInMonths } = useMemo(() => {
    const filteredOffering = quote.quoteOfferings.find(
      ({ id }) => id === offeringId,
    );

    if (filteredOffering) {
      const {
        billingFrequencyInMonths: billingFrequency,
        startDate,
        endDate,
      } = filteredOffering;
      return {
        billingFrequencyInMonths: billingFrequency,
        offeringPeriod: `${toDateShort(startDate)} - ${toDateShort(endDate)}`,
      };
    }

    return {
      offeringPeriod: '',
      billingFrequencyInMonths: 12,
    };
  }, [offeringId, quote.quoteOfferings]);

  const isCustomDiscountExcludedFromRenewal =
    useCustomDiscountExcludedOnRenewal(itemFromServer);

  const anyQuoteItemHasCatalogDiscount = useMemo(() => {
    return quote.quoteOfferings
      .flatMap(({ items }) => items)
      .some(({ discounts }) => (discounts?.length ?? 0) > 0);
  }, [quote.quoteOfferings]);

  const discountExcludedOnRenewal =
    isAnyDiscountExcludedOnRenewal(itemFromServer) ||
    isCustomDiscountExcludedFromRenewal;

  const sortedDiscounts = useMemo(() => {
    const discounts = [...itemFromServer.discounts];

    if (!discounts || !discounts.length) {
      return [];
    }

    discounts.sort((a, b) => sortAlphabetically('discountName')(a, b));

    return discounts;
  }, [itemFromServer.discounts]);

  const { catalogText, customText } = useMemo(() => {
    const catalogType =
      multipleDiscountType(itemFromServer.discounts) ||
      AmountUnitTypeEnum.PERCENTAGE;
    const catalogTotal =
      catalogType === AmountUnitTypeEnum.FLAT
        ? itemFromServer.discountAmount
        : totalCatalogDiscount;
    return {
      catalogText: getDiscountText(catalogTotal, catalogType, quote.currency),
      customText: getDiscountText(
        itemFromServer.customDiscountAmountOrPercent,
        itemFromServer.customDiscountType ?? AmountUnitTypeEnum.PERCENTAGE,
        quote.currency,
      ),
    };
  }, [itemFromServer, totalCatalogDiscount, quote.currency]);

  const isSequential =
    quote.discountApplicationMethod ===
    DiscountApplicationMethodEnum.SEQUENTIAL;
  const effectiveDiscountPercent =
    itemFromServer.productType === ProductTypeEnum.USAGE ||
    quote.discountApplicationMethod === DiscountApplicationMethodEnum.COMBINED
      ? getDiscountPercentForQuoteItem(itemFromServer, isSequential) * 100
      : itemFromServer.discountPercent;
  const discountText = effectiveDiscountPercent
    ? formatPercentNumerator(effectiveDiscountPercent, {
        maximumFractionDigits: 2,
      })
    : '';
  const isTooltipDisabled = !discountText;

  /**
   * When catalog discounts are in play, the combined figure rendered beside
   * this cell already includes the custom one. Rendering both put two
   * percentages side by side with no way to tell which was which.
   */
  const showCustomDiscountValue =
    !!customText && !(hasCatalogDiscounts && !!discountText);

  const discountTooltip = (
    <MFlex flexDirection="column">
      <MText color="white" fontStyle="italic">
        Discounts Applied:
      </MText>
      {sortedDiscounts.map((discount, discountIndex) => {
        const {
          discountName: name,
          discountType: type,
          discountAmountOrPercent: value,
          discountLifecyclePolicies: lifecyclePolicies,
        } = discount;

        const excludeFromRenewal = lifecyclePolicies?.includes(
          DiscountLifeCyclePolicyEnum.EXCLUDE_FROM_RENEWAL,
        );

        return (
          <MText
            key={`${name}-${discountIndex}`}
            color="white"
            fontWeight="bold"
          >
            {`${name} - ${type === 'PERCENTAGE' ? `${value}%` : `$${value}`}`}
            {excludeFromRenewal ? ' *' : ''}
          </MText>
        );
      })}

      {!!itemFromServer.customDiscountAmountOrPercent && (
        <>
          <MDivider my={1} />
          <MText color="white" fontWeight="bold">
            {`Custom Discount - ${itemFromServer.customDiscountType === 'PERCENTAGE' ? `${itemFromServer.customDiscountAmountOrPercent}%` : `$${itemFromServer.customDiscountAmountOrPercent}`}`}
            {isCustomDiscountExcludedFromRenewal ? ' *' : ''}
          </MText>
        </>
      )}
      {discountExcludedOnRenewal && (
        <MText color="white" fontStyle="italic" fontSize="xs">
          * Removed upon renewal
        </MText>
      )}
      {hasCatalogDiscounts &&
        !!itemFromServer.customDiscountAmountOrPercent && (
          <>
            <MDivider my={1} />
            <MText color="white" fontStyle="italic" fontSize="xs">
              {quote.discountApplicationMethod ===
              DiscountApplicationMethodEnum.SEQUENTIAL
                ? `Custom discount applied first (${customText}), then catalog discount applied to reduced price (${catalogText})`
                : `Catalog and custom discounts have been combined (${catalogText} + ${customText} = ${discountText})`}
            </MText>
          </>
        )}
    </MFlex>
  );

  const additionalMenuItems = useMemo(() => {
    if (!customDiscountAmountOrPercent) {
      return [];
    }

    return [
      <MText
        key="edit-discount"
        onClick={onModalOpen}
        cursor="pointer"
        fontSize="sm"
        fontWeight="normal"
        _hover={{ color: 'tPurple.dark' }}
      >
        Edit Discount
      </MText>,
    ];
  }, [customDiscountAmountOrPercent, onModalOpen]);

  return (
    <>
      <MFlex data-testid="discount-cell" alignItems="center">
        <MFormField
          error={errors?.items?.[index]?.customDiscountAmountOrPercent}
          alignSelf="center"
          /**
           * Editable rows reserve a fixed column so every row's combined figure
           * lines up to the right of its input. Read-only rows have no input to
           * reserve for, and have to shrink explicitly — FormControl's own
           * `width: 100%` would otherwise fill the cell and push the value to
           * the far right of the column.
           */
          w={
            isReadOnly
              ? 'auto'
              : anyQuoteItemHasCatalogDiscount
                ? '5rem'
                : undefined
          }
          flexShrink={0}
          data-testid="discount-input-cell"
        >
          {isReadOnly ? (
            /**
             * A locked discount reads as plain text — an input nobody can use
             * was the only boxed control left in the row once quantity went
             * read-only, and an empty one was pure noise. The MFormField
             * wrapper stays either way so rows with catalog discounts keep
             * their alignment.
             */
            showCustomDiscountValue && (
              <MText
                fontWeight="400"
                /** Matches the combined figure's `ml` so the two line up. */
                ps={2}
              >
                {customText}
              </MText>
            )
          ) : (
            <Controller
              control={control}
              name={`items.${index}.customDiscountAmountOrPercent`}
              render={({ field: { value, onChange, onBlur } }) => (
                <DiscountCell
                  isDisabled={isDisabled}
                  item={itemFromServer}
                  quote={quote}
                  discountType={
                    customDiscountType || AmountUnitTypeEnum.PERCENTAGE
                  }
                  value={value ? value.toString() : ''}
                  ref={discountAmountRef}
                  onChangeAmountUnitType={onChangeDiscountType}
                  onChange={(valueAsString: string, valueAsNumber: number) => {
                    onChange(valueAsString);
                  }}
                  onBlur={() => {
                    onBlur();
                    handleQuoteOffering();
                  }}
                  targetAmountOptions={{
                    amountWithoutDiscount: targetAmount,
                    billingFrequencyInMonths,
                    contractLength: quote.contractLength,
                    currency: quote.currency,
                    discountPercent: itemFromServer.discountPercent,
                    offeringPeriod,
                    productName: itemFromServer.productName,
                    targetAmount: itemFromServer.amount,
                    productType: itemFromServer.productType,
                    displayUnitPrice: itemFromServer.displayUnitPrice,
                    displayUnitPriceFrequency:
                      itemFromServer.options?.displayUnitPriceFrequency,
                    customDiscountAmountOrPercent:
                      itemFromServer.customDiscountAmountOrPercent,
                    catalogDiscountTotal: totalCatalogDiscount,
                    isUsageProduct:
                      itemFromServer.productType === ProductTypeEnum.USAGE,
                    discountApplicationMethod: quote.discountApplicationMethod,
                  }}
                  disableUnitTypeSelect={disableUnitTypeSelect}
                  disableUnitTypeMessage={disableUnitTypeMessage}
                  additionalMenuItems={additionalMenuItems}
                />
              )}
            />
          )}
        </MFormField>
        {isCustomDiscountExcludedFromRenewal && !hasCatalogDiscounts && (
          <MTooltip
            label="This discount will be removed upon renewal"
            placement="bottom-end"
          >
            <MFlex align="center">
              <MIcon
                as={LuRefreshCwOff}
                boxSize={4}
                color="tPurple.dark"
                ml={2}
                cursor="pointer"
              />
            </MFlex>
          </MTooltip>
        )}
        {hasCatalogDiscounts && (
          <MTooltip
            isDisabled={isTooltipDisabled}
            label={discountTooltip}
            placement="bottom-start"
          >
            <MFlex
              ml={2}
              _hover={{ cursor: isTooltipDisabled ? 'initial' : 'pointer' }}
            >
              <MFlex alignItems="center" gap={1}>
                <MText fontWeight="400" alignSelf="center">
                  {discountText}
                </MText>
              </MFlex>
            </MFlex>
          </MTooltip>
        )}
      </MFlex>

      {/* Edit Discount Modal */}
      <QuoteItemDiscountEditModal
        isOpen={isModalOpen}
        onClose={onModalClose}
        control={control}
        errors={errors}
        setValue={setValue}
        index={index}
        disabled={isDisabled}
        itemFromServer={itemFromServer}
        handleQuoteOffering={handleQuoteOffering}
      />
    </>
  );
};
